import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import styled, { ThemeContext } from "styled-components";
import { Accordion, AccordionBody, AccordionHeader, SubSection, useBreakpoints } from "best-common-react";
import { useProspectProfile } from "../../../contexts/ProsepctProfileContext";
import useLanguage from "../../../hooks/useLanguage";
import { useMetadata } from "../../../contexts/MetadataContext";
import { Card, Item, Label, Row, Value } from "./ReadOnlyProfileStyles";
import { CountryIds } from "../../../constants/CountryConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { ForeignEducationSchoolDTO } from "../../../types/ProspectProfile";
import { formatLocalDate } from "../../../util/ProfileUtils";

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

type ForeignSchoolReadOnlyCardProps = {
  foreignSchool: ForeignEducationSchoolDTO;
};

const ForeignSchoolReadOnlyCard: React.FC<ForeignSchoolReadOnlyCardProps> = ({ foreignSchool }) => {
  const { t } = useTranslation("profile");

  return (
    <Card>
      <CardContent>
        <div>
          <Label>{t("profile:foreignSchoolName")}</Label>
          <Value>{foreignSchool.schoolName}</Value>
        </div>
        <div>
          <Label>{t("profile:dateOfLastClass")}</Label>
          <Value>{formatLocalDate(foreignSchool.lastClassDate)}</Value>
        </div>
      </CardContent>
    </Card>
  );
};

const EducationInfoReadOnly: React.FC = () => {
  const { t } = useTranslation(["common", "profile", "translation"]);
  const Theme = useContext(ThemeContext);
  const { isMobile } = useBreakpoints();
  const { profile } = useProspectProfile();
  const { isEs } = useLanguage();
  const { academicLevels } = useMetadata();
  const { getYesNoRadioValues } = useDropdowns();

  const prospectAcademicLevel = academicLevels?.find(
    (al) => al.academicLevelId === profile.highestProspectAcademicLevelId,
  );
  const parentAcademicLevel = academicLevels?.find((al) => al.academicLevelId === profile.highestParentAcademicLevelId);

  return (
    <Accordion
      accordionKey="basball-info"
      styles={{ ...Theme.accordion, body: { ...Theme.accordion.body, background: Theme.colors.cloud, border: "none" } }}
    >
      <AccordionHeader>{t("profile:educationSection")}</AccordionHeader>
      <AccordionBody>
        <Row>
          <Item isMobile={isMobile}>
            <Label>{t("profile:currentlyAttending")}</Label>
            <Value>{profile?.currentlyAttendingSchool ? t("translation:yes") : t("translation:no")}</Value>
          </Item>
        </Row>
        <Row>
          <Item isMobile={isMobile}>
            <Label>{t("profile:highestGradeQuestion")}</Label>
            <Value>{isEs ? prospectAcademicLevel?.esDescription : prospectAcademicLevel?.description}</Value>
            {profile?.highestProspectAcademicLevelId === 4 && (
              <Value>{profile.highestProspectAcademicLevelWriteIn}</Value>
            )}
            <Value>{profile?.highestProspectAcademicLevelYear}</Value>
          </Item>
        </Row>
        <Row>
          <Item isMobile={isMobile}>
            <Label>{t("profile:highestParentGrade")}</Label>
            <Value>{isEs ? parentAcademicLevel?.esDescription : parentAcademicLevel?.description}</Value>
            {profile?.highestParentAcademicLevelId === 4 && <Value>{profile.highestParentAcademicLevelWriteIn}</Value>}
          </Item>
        </Row>

        {profile?.birthCountryId === CountryIds.CUBA && (
          <>
            <Row>
              <div>
                <Label>{t("profile:hasForeignEducationQuestion")}</Label>
                <Value>{getYesNoRadioValues(profile?.hasForeignEducation)?.label}</Value>
              </div>
            </Row>
            {profile?.hasForeignEducation === true && (
              <SubSection header={t("profile:schools")}>
                {profile?.foreignSchools.map((school) => (
                  <ForeignSchoolReadOnlyCard
                    key={`foreign-school-card-${school.foreignEducationSchoolId}`}
                    foreignSchool={school}
                  />
                ))}
              </SubSection>
            )}
          </>
        )}
      </AccordionBody>
    </Accordion>
  );
};

export default EducationInfoReadOnly;
